import React, { useCallback, useMemo, useRef, useState } from 'react';
import ConfirmationModal from 'src/components/ConfirmationModal';
import styles from './styles.module.scss';
import {
    COLLAPSE_THRESHOLD,
    ExtractionSchemaRecord,
    ExtractionValueType,
    SchemaPath,
    buildSchemaForType,
    collectGroupPaths,
    countNestedFields,
    countSchemaFields,
    describeSchemaSummary,
    deserializePath,
    filterSchemaFields,
    getSchemaAtPath,
    getSchemaProperties,
    getSchemaRequired,
    getUniqueGeneratedKey,
    getUniquePropertyKey,
    getUnsupportedSchemaIssues,
    getValueDefinition,
    getValueTypeLabel,
    isRecord,
    moveProperty,
    normalizeManagedSchema,
    serializePath,
    unwrapSchemaArrays,
    updateSchemaAtPath,
    wrapSchemaAsArray,
} from './extractionSchema/schemaModel';
import { EditorActions, EditorUiState, FieldDraft } from './extractionSchema/editorTypes';
import useStableFieldIds from './extractionSchema/useStableFieldIds';
import FieldList from './extractionSchema/FieldList';

export type { ExtractionSchemaRecord, ExtractionSchemaType } from './extractionSchema/schemaModel';
export { getUnsupportedSchemaIssues, normalizeManagedSchema } from './extractionSchema/schemaModel';

interface ExtractionSchemaEditorProps {
    schema: ExtractionSchemaRecord | null;
    editable: boolean;
    onChange: (schema: ExtractionSchemaRecord) => void;
}

interface PendingDestructiveChange {
    title: string;
    confirmText: string;
    onConfirm: () => void;
}

const DEFAULT_ROOT: ExtractionSchemaRecord = { type: 'object', additionalProperties: false, properties: {} };

export default function ExtractionSchemaEditor({ schema, editable, onChange }: ExtractionSchemaEditorProps) {
    const rootSchema = schema || DEFAULT_ROOT;
    const unsupportedIssues = getUnsupportedSchemaIssues(rootSchema);

    const fieldIds = useStableFieldIds();
    // rootRef tracks the latest schema: it follows the schema prop, but
    // commitSchema echoes commits into it eagerly so same-tick follow-ups
    // (e.g. the editor card's unmount safety commit) read the committed state
    // instead of the stale prop and stay idempotent.
    const rootRef = useRef(rootSchema);
    const lastSchemaPropRef = useRef(rootSchema);
    if (lastSchemaPropRef.current !== rootSchema) {
        lastSchemaPropRef.current = rootSchema;
        rootRef.current = rootSchema;
    }
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    // Manual key overrides only inform commit-time auto-key generation, so a
    // plain ref (keyed by stable field id) avoids re-renders entirely.
    const overriddenKeysRef = useRef<Set<string>>(new Set());

    const [pendingChange, setPendingChange] = useState<PendingDestructiveChange | null>(null);
    const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(() => {
        if (countSchemaFields(rootSchema).fields > COLLAPSE_THRESHOLD) {
            return new Set();
        }
        return new Set(collectGroupPaths(rootSchema).map((path) => fieldIds.getId(serializePath(path))));
    });

    const commitSchema = useCallback((nextSchema: ExtractionSchemaRecord) => {
        const normalized = normalizeManagedSchema(nextSchema);
        rootRef.current = normalized;
        onChangeRef.current(normalized);
    }, []);

    const updateAtPath = useCallback(
        (path: SchemaPath, updater: (current: ExtractionSchemaRecord) => ExtractionSchemaRecord) => {
            commitSchema(updateSchemaAtPath(rootRef.current, path, updater));
        },
        [commitSchema],
    );

    const expandGroup = useCallback((fieldId: string) => {
        setExpandedGroupIds((current) => {
            if (current.has(fieldId)) {
                return current;
            }
            const next = new Set(current);
            next.add(fieldId);
            return next;
        });
    }, []);

    const addField = useCallback(
        (serializedObjectPath: string) => {
            const objectPath = deserializePath(serializedObjectPath);
            const objectSchema = getSchemaAtPath(rootRef.current, objectPath);
            const propertyKey = getUniquePropertyKey(getSchemaProperties(objectSchema));
            updateAtPath(objectPath, (currentObject) => ({
                ...currentObject,
                type: 'object',
                properties: { ...getSchemaProperties(currentObject), [propertyKey]: { type: 'string' } },
            }));
            // Reveal the new field: expand the group it was added to and open
            // its editor card with the name input ready.
            const ownerPath = [...objectPath];
            while (ownerPath.length && ownerPath[ownerPath.length - 1].kind === 'items') {
                ownerPath.pop();
            }
            if (ownerPath.length) {
                expandGroup(fieldIds.getId(serializePath(ownerPath)));
            }
            setActiveFieldId(fieldIds.getId(serializePath(objectPath, propertyKey)));
        },
        [updateAtPath, fieldIds, expandGroup],
    );

    const performRename = useCallback(
        (objectPath: SchemaPath, currentKey: string, nextKey: string): string | null => {
            if (!nextKey) {
                return 'Property key is required.';
            }
            const objectSchema = getSchemaAtPath(rootRef.current, objectPath);
            const properties = getSchemaProperties(objectSchema);
            if (nextKey !== currentKey && Object.prototype.hasOwnProperty.call(properties, nextKey)) {
                return 'Property keys must be unique at this level.';
            }
            if (nextKey === currentKey) {
                return null;
            }
            updateAtPath(objectPath, (currentObject) => {
                const currentProperties = getSchemaProperties(currentObject);
                const renamedProperties = Object.entries(currentProperties).reduce((result, [key, value]) => {
                    result[key === currentKey ? nextKey : key] = value;
                    return result;
                }, {} as ExtractionSchemaRecord);
                const required = getSchemaRequired(currentObject).map((key) => (key === currentKey ? nextKey : key));
                return {
                    ...currentObject,
                    properties: renamedProperties,
                    ...(required.length ? { required } : { required: undefined }),
                };
            });
            fieldIds.handleRename(serializePath(objectPath, currentKey), serializePath(objectPath, nextKey));
            return null;
        },
        [updateAtPath, fieldIds],
    );

    const renameKey = useCallback(
        (serializedObjectPath: string, currentKey: string, nextKey: string): string | null => {
            const objectPath = deserializePath(serializedObjectPath);
            const error = performRename(objectPath, currentKey, nextKey);
            if (!error) {
                overriddenKeysRef.current.add(fieldIds.getId(serializePath(objectPath, nextKey)));
            }
            return error;
        },
        [performRename, fieldIds],
    );

    const commitFieldEdits = useCallback(
        (serializedObjectPath: string, propertyKey: string, draft: FieldDraft) => {
            const objectPath = deserializePath(serializedObjectPath);
            const objectSchema = getSchemaAtPath(rootRef.current, objectPath);
            const properties = getSchemaProperties(objectSchema);
            const propertySchema = properties[propertyKey];
            if (!isRecord(propertySchema)) {
                return;
            }
            const currentTitle = typeof propertySchema.title === 'string' ? propertySchema.title : '';
            const currentDescription = typeof propertySchema.description === 'string' ? propertySchema.description : '';
            const fieldId = fieldIds.getId(serializePath(objectPath, propertyKey));
            let nextKey = propertyKey;
            if (!overriddenKeysRef.current.has(fieldId)) {
                const generated = getUniqueGeneratedKey(properties, propertyKey, draft.title);
                if (generated) {
                    nextKey = generated;
                }
            }
            if (currentTitle === draft.title && currentDescription === draft.description && nextKey === propertyKey) {
                return;
            }
            updateAtPath(objectPath, (currentObject) => {
                const currentProperties = getSchemaProperties(currentObject);
                const nextProperties = Object.entries(currentProperties).reduce((result, [key, value]) => {
                    if (key === propertyKey && isRecord(value)) {
                        const nextProperty = { ...value };
                        if (draft.title.trim()) {
                            nextProperty.title = draft.title;
                        } else {
                            delete nextProperty.title;
                        }
                        if (draft.description.trim()) {
                            nextProperty.description = draft.description;
                        } else {
                            delete nextProperty.description;
                        }
                        result[nextKey] = nextProperty;
                    } else {
                        result[key] = value;
                    }
                    return result;
                }, {} as ExtractionSchemaRecord);
                const required = getSchemaRequired(currentObject).map((key) => (key === propertyKey ? nextKey : key));
                return {
                    ...currentObject,
                    properties: nextProperties,
                    ...(required.length ? { required } : { required: undefined }),
                };
            });
            if (nextKey !== propertyKey) {
                fieldIds.handleRename(serializePath(objectPath, propertyKey), serializePath(objectPath, nextKey));
            }
        },
        [updateAtPath, fieldIds],
    );

    const removeField = useCallback(
        (serializedObjectPath: string, propertyKey: string, displayName: string) => {
            const objectPath = deserializePath(serializedObjectPath);
            const objectSchema = getSchemaAtPath(rootRef.current, objectPath);
            const propertySchema = getSchemaProperties(objectSchema)[propertyKey];
            if (!isRecord(propertySchema)) {
                return;
            }
            const serializedPropertyPath = serializePath(objectPath, propertyKey);
            const removedId = fieldIds.getId(serializedPropertyPath);
            const performRemove = () => {
                updateAtPath(objectPath, (currentObject) => {
                    const nextProperties = { ...getSchemaProperties(currentObject) };
                    delete nextProperties[propertyKey];
                    const required = getSchemaRequired(currentObject).filter((key) => key !== propertyKey);
                    return {
                        ...currentObject,
                        properties: nextProperties,
                        ...(required.length ? { required } : { required: undefined }),
                    };
                });
                fieldIds.handleRemove(serializedPropertyPath);
                setActiveFieldId((current) => (current === removedId ? null : current));
            };
            const nestedCount = countNestedFields(propertySchema);
            if (nestedCount > 0) {
                setPendingChange({
                    title: `Remove “${displayName}”? The ${nestedCount} ${
                        nestedCount === 1 ? 'field' : 'fields'
                    } inside it will also be removed.`,
                    confirmText: 'Remove field',
                    onConfirm: performRemove,
                });
                return;
            }
            performRemove();
        },
        [updateAtPath, fieldIds],
    );

    const moveField = useCallback(
        (serializedObjectPath: string, propertyKey: string, offset: -1 | 1) => {
            const objectPath = deserializePath(serializedObjectPath);
            updateAtPath(objectPath, (currentObject) => moveProperty(currentObject, propertyKey, offset));
        },
        [updateAtPath],
    );

    const toggleRequired = useCallback(
        (serializedObjectPath: string, propertyKey: string, required: boolean) => {
            const objectPath = deserializePath(serializedObjectPath);
            updateAtPath(objectPath, (objectSchema) => {
                const nextRequired = new Set(getSchemaRequired(objectSchema));
                if (required) {
                    nextRequired.add(propertyKey);
                } else {
                    nextRequired.delete(propertyKey);
                }
                const requiredNames = Array.from(nextRequired);
                return {
                    ...objectSchema,
                    ...(requiredNames.length ? { required: requiredNames } : { required: undefined }),
                };
            });
        },
        [updateAtPath],
    );

    const changeFieldShape = useCallback(
        (serializedPropertyPath: string, displayName: string, nextType: ExtractionValueType, nextMultiple: boolean) => {
            const propertyPath = deserializePath(serializedPropertyPath);
            const currentSchema = getSchemaAtPath(rootRef.current, propertyPath);
            const { arrayDepth, type: currentType } = getValueDefinition(currentSchema, propertyPath);
            const currentMultiple = arrayDepth > 0;
            if (currentType === nextType && currentMultiple === nextMultiple) {
                return;
            }
            const perform = () => {
                updateAtPath(propertyPath, (value) => {
                    const unwrapped = unwrapSchemaArrays(value);
                    const retyped =
                        getValueDefinition(value, []).type === nextType
                            ? unwrapped
                            : buildSchemaForType(nextType, unwrapped);
                    return nextMultiple ? wrapSchemaAsArray(retyped) : retyped;
                });
                if (nextType === 'object') {
                    expandGroup(fieldIds.getId(serializePath(propertyPath)));
                }
            };
            const nestedCount = countNestedFields(currentSchema);
            if (currentType !== nextType && currentType === 'object' && nestedCount > 0) {
                setPendingChange({
                    title: `Changing “${displayName}” to ${getValueTypeLabel(
                        nextType,
                    ).toLowerCase()} removes the ${nestedCount} ${nestedCount === 1 ? 'field' : 'fields'} inside it.`,
                    confirmText: 'Change type',
                    onConfirm: perform,
                });
                return;
            }
            if (arrayDepth > 1) {
                setPendingChange({
                    title: `Updating “${displayName}” removes its nested list levels.`,
                    confirmText: nextMultiple ? 'Update field' : 'Use one item',
                    onConfirm: perform,
                });
                return;
            }
            perform();
        },
        [updateAtPath, fieldIds, expandGroup],
    );

    const toggleGroup = useCallback((fieldId: string) => {
        setExpandedGroupIds((current) => {
            const next = new Set(current);
            if (next.has(fieldId)) {
                next.delete(fieldId);
            } else {
                next.add(fieldId);
            }
            return next;
        });
    }, []);

    const openEditor = useCallback((fieldId: string) => setActiveFieldId(fieldId), []);
    const closeEditor = useCallback(() => setActiveFieldId(null), []);

    const expandAll = useCallback(() => {
        setExpandedGroupIds(
            new Set(collectGroupPaths(rootRef.current).map((path) => fieldIds.getId(serializePath(path)))),
        );
    }, [fieldIds]);
    const collapseAll = useCallback(() => setExpandedGroupIds(new Set()), []);

    const getFieldId = useCallback((serializedPath: string) => fieldIds.getId(serializedPath), [fieldIds]);

    const actions: EditorActions = useMemo(
        () => ({
            addField,
            removeField,
            moveField,
            toggleGroup,
            openEditor,
            closeEditor,
            commitFieldEdits,
            changeFieldShape,
            toggleRequired,
            renameKey,
            getFieldId,
        }),
        [
            addField,
            removeField,
            moveField,
            toggleGroup,
            openEditor,
            closeEditor,
            commitFieldEdits,
            changeFieldShape,
            toggleRequired,
            renameKey,
            getFieldId,
        ],
    );

    const filter = useMemo(() => filterSchemaFields(rootSchema, searchQuery), [rootSchema, searchQuery]);

    if (unsupportedIssues.length) {
        return (
            <div className={styles.extractionSchemaUnsupported} data-test-id="extraction-schema-unsupported">
                <strong>This schema uses advanced settings the visual builder can’t edit.</strong>
                <ul className={styles.extractionSchemaUnsupportedIssues}>
                    {unsupportedIssues.map((issue) => (
                        <li key={issue} data-test-id="extraction-schema-unsupported-issue">
                            {issue}
                        </li>
                    ))}
                </ul>
                <small>
                    It’s safe — the existing schema is preserved exactly as-is when other document type settings are
                    saved.
                </small>
            </div>
        );
    }

    const { fields: totalFields, groups: totalGroups } = countSchemaFields(rootSchema);
    const ui: EditorUiState = { editable, activeFieldId, expandedGroupIds, filter };

    return (
        <div className={styles.extractionSchemaBuilder} data-test-id="extraction-schema-builder">
            <div className={styles.extractionSchemaHeader}>
                <div className={styles.extractionSchemaHeaderText}>
                    <strong>Fields to capture</strong>
                    <span>Each field is one piece of information Capture AI reads from the document images.</span>
                </div>
                <div className={styles.extractionSchemaHeaderMeta}>
                    <span data-test-id="extraction-schema-summary">{describeSchemaSummary(rootSchema)}</span>
                    {totalGroups > 0 ? (
                        <span className={styles.extractionSchemaHeaderActions}>
                            <button type="button" onClick={expandAll} data-test-id="extraction-schema-expand-all">
                                Expand all
                            </button>
                            <button type="button" onClick={collapseAll} data-test-id="extraction-schema-collapse-all">
                                Collapse all
                            </button>
                        </span>
                    ) : null}
                </div>
            </div>
            {editable && totalFields > 0 ? (
                <div className={styles.extractionSchemaSearch}>
                    <input
                        type="search"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search fields…"
                        aria-label="Search fields"
                        data-test-id="extraction-schema-search"
                    />
                    {filter ? (
                        <span data-test-id="extraction-schema-search-count">
                            {filter.matchCount} {filter.matchCount === 1 ? 'match' : 'matches'}
                        </span>
                    ) : null}
                </div>
            ) : null}
            <FieldList
                objectSchema={rootSchema}
                serializedObjectPath={serializePath([])}
                depth={0}
                ui={ui}
                actions={actions}
            />
            {pendingChange ? (
                <ConfirmationModal
                    headerTitle="Are you sure?"
                    title={pendingChange.title}
                    confirmButtonText={pendingChange.confirmText}
                    onCancel={() => setPendingChange(null)}
                    onConfirm={() => {
                        const onConfirm = pendingChange.onConfirm;
                        setPendingChange(null);
                        onConfirm();
                    }}
                />
            ) : null}
        </div>
    );
}
